/**
 * The app's page-wide status strip — ROUTE_INVENTORY Part 1, G6–G9.
 *
 * **One at a time, by priority.** All four banner hooks run here, and
 * `pickShellBanner` chooses the highest active one: session expired › offline
 * › service issues › billing (action required › past due). Only that one is
 * rendered. When it clears or is dismissed the next active one appears — a
 * dismissal takes a banner out of the running, it does not hide the rest.
 * Each banner keeps its own dismissal rule; see its hook.
 *
 * They used to stack, four rows deep in the worst case, which pushed the page
 * down by up to ~200px to tell the user four things of which only the first
 * mattered (nothing works offline, so a billing reminder is noise). Before
 * THAT they overlapped, all `fixed top-16` at different z-indexes.
 *
 * Every strip is drawn by `core/common/banners/PageBanner`, so whichever one
 * shows, the page moves by the same one row.
 *
 * **It pushes the page down rather than covering it.** The container
 * publishes its height as `--shell-banners-height` on `<html>` (0 when empty),
 * and `AppLayout` adds it to the navbar offset.
 *
 * **Live regions.** The offline strip is polite and needs a region that
 * already exists when its message arrives, so a permanent `role='status'`
 * wrapper is always mounted and only the offline strip ever renders into it.
 * The other three are `role='alert'` and are announced on insertion.
 *
 * Lives in `features/` because it composes `BillingActionBanner` (and `core/`
 * may not import features), and not in `app/` because nothing but `App.tsx`
 * may import from there — including the stories, which mount the same
 * component through `withShellBanners`.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { OfflineStatusRegion } from '@/components/core/common/banners/OfflineBanner';
import { ServiceIssuesBannerView } from '@/components/core/common/banners/ServiceIssuesBanner';
import { SessionExpiredBannerView } from '@/components/core/common/banners/SessionExpiredBanner';
import { BillingActionBannerView } from '@/components/features/billing/cards/BillingActionBanner';
import { useBillingBanner } from '@/hooks/account/useBillingBanner';
import { useOfflineBanner } from '@/hooks/ui/banners/useOfflineBanner';
import { useServiceIssuesBanner } from '@/hooks/ui/banners/useServiceIssuesBanner';
import { useSessionExpiredBanner } from '@/hooks/ui/banners/useSessionExpiredBanner';
import { pickShellBanner } from '@/lib/banners/pickShellBanner';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';

/** Read by `AppLayout` to offset its content below the strip. */
const SHELL_BANNERS_HEIGHT_VAR = '--shell-banners-height';

/**
 * Keep `--shell-banners-height` equal to the container's rendered height. A
 * ResizeObserver rather than a constant: the strip wraps on narrow screens.
 */
function usePublishedHeight(overlay: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    const root = document.documentElement;
    // Floating over the page (a phone): it takes no room, so it publishes
    // none, and the page under it does not move when one appears.
    if (overlay) {
      root.style.setProperty(SHELL_BANNERS_HEIGHT_VAR, '0px');
      return () => root.style.removeProperty(SHELL_BANNERS_HEIGHT_VAR);
    }
    if (!el || typeof ResizeObserver === 'undefined') return;
    const publish = () =>
      root.style.setProperty(SHELL_BANNERS_HEIGHT_VAR, `${el.offsetHeight}px`);
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    publish();
    return () => {
      observer.disconnect();
      root.style.removeProperty(SHELL_BANNERS_HEIGHT_VAR);
    };
  }, [overlay]);
  return ref;
}

/**
 * The one page-wide banner, by priority.
 *
 * Beside `sm` it is a strip under the navbar, and the page moves down to make
 * room (`--shell-banners-height`). On a phone it FLOATS: a card over the top
 * of the page (`PageBanner`'s phone form), and the page stays where it is -
 * a 90px strip pushing every screen down was a tenth of the phone. Floating,
 * every banner can be closed, the offline one included (it comes back if the
 * connection drops again).
 */
export function ShellBanners() {
  const phone = useMediaQuery('(max-width: 639.98px)');
  const ref = usePublishedHeight(phone);
  const location = useLocation();
  const session = useSessionExpiredBanner();
  const offline = useOfflineBanner();
  const service = useServiceIssuesBanner();
  const billing = useBillingBanner();

  // Closed for as long as THIS outage lasts; a new one shows it again.
  const [offlineClosed, setOfflineClosed] = useState(false);
  useEffect(() => {
    if (!offline.raised) setOfflineClosed(false);
  }, [offline.raised]);

  // On the Payment section itself the billing banner only pointed at the page
  // you were on ("Update payment method" -> here); its status badge and the
  // card list already say the same thing, with the controls beside them.
  // A phone only: there the URL follows the open section, so `?tab=payment`
  // IS the Payment screen. Beside `md` Settings keeps the URL as it entered,
  // and the banner keeps its desktop behaviour.
  const onPayment =
    phone &&
    location.pathname.startsWith('/settings') &&
    new URLSearchParams(location.search).get('tab') === 'payment';

  const shown = pickShellBanner({
    sessionExpired: { raised: session.raised, dismissed: session.dismissed },
    offline: { raised: offline.raised && !(phone && offlineClosed) },
    serviceIssues: { raised: service.raised, dismissed: service.dismissed },
    billing: { kind: billing.kind, dismissed: billing.dismissed || onPayment },
  });

  return (
    <div
      ref={ref}
      data-testid='shell-banners'
      data-shown={shown ?? 'none'}
      // Under the navbar: `--app-navbar-height` is 4rem, or 0 on a page that
      // hides the bar on a phone (`AppLayout`). `z-40` sits under the
      // navbar's own popovers and over page content. Empty, it is zero-height.
      className='fixed inset-x-0 top-[var(--app-navbar-height,4rem)] z-40'
    >
      {shown === 'session-expired' && (
        <SessionExpiredBannerView
          onSignInAgain={() => void session.signInAgain()}
          onDismiss={session.dismiss}
        />
      )}
      <OfflineStatusRegion
        show={shown === 'offline'}
        onDismiss={phone ? () => setOfflineClosed(true) : undefined}
      />
      {shown === 'service-issues' && (
        <ServiceIssuesBannerView onDismiss={service.dismiss} />
      )}
      {(shown === 'billing-action-required' || shown === 'billing-past-due') &&
        billing.kind && (
          <BillingActionBannerView
            kind={billing.kind}
            hostedUrl={billing.hostedUrl}
            onDismiss={billing.dismiss}
          />
        )}
    </div>
  );
}
